import { reduce, isPlainObject, compact, map, find, get, isEmpty, omit, pick, includes } from 'lodash';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const mergeArrays = (firstArray, secondArray) => {
  if (!Array.isArray(firstArray)) {
    if (!Array.isArray(secondArray)) return [];
    return [...secondArray];
  }

  return [...firstArray, ...secondArray];
};
const isPresent = value => value !== undefined && value !== null && value !== '';
const partition = (object, func) => reduce(object, (partitions, value, key) => {
  const partitionIndex = func(value, key) ? 0 : 1;
  partitions[partitionIndex][key] = value;
  return partitions;
}, [{}, {}]);

const extractIdentifier = resource => {
  const name = isPresent(resource.id) ? 'id' : isPresent(resource.lid) ? 'lid' : undefined;
  const value = isPresent(resource[name]) ? String(resource[name]) : undefined;
  return {
    name,
    valid: name !== undefined && value !== undefined,
    value
  };
};
const renderIdentifier = _ref => {
  let {
    name,
    value,
    valid
  } = _ref;
  if (!valid) return {};
  return {
    [name]: value
  };
};
const COMPEON_API_JS_TYPE = Symbol('COMPEON_API_JS_TYPE');
const withPolymorphicType = (type, obj) => {
  if (Array.isArray(obj)) {
    return obj.map(entry => withPolymorphicType(type, entry));
  } else {
    return Object.assign(obj, {
      [COMPEON_API_JS_TYPE]: type
    });
  }
};

const findInclude = (type, identifier, included) => find(included, _objectSpread2(_objectSpread2({}, renderIdentifier(identifier)), {}, {
  type
}));

const renderLinks = function (links) {
  let key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'links';
  if (!links) return null;
  return {
    [key]: links
  };
};

const deserializeRelationships = (relationships, included) => reduce(relationships, (result, value, key) => {
  const data = get(value, 'data');
  const links = get(value, 'links');
  let deserializedResource = null;

  if (Array.isArray(data)) {
    deserializedResource = deserializeResources(data, included, links);
  } else if (isPlainObject(data)) {
    deserializedResource = deserializeResource(data, included, links);
    if (isEmpty(deserializedResource)) deserializedResource = null;
  }

  result[key] = deserializedResource;
  return result;
}, {});

const deserializeResource = function (resource, included, rootLinks) {
  let root = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  const identifier = extractIdentifier(resource);
  const {
    attributes,
    links,
    relationships,
    type
  } = resource;
  const {
    attributes: includedAttributes,
    links: includedLinks,
    relationships: includedRelationships
  } = findInclude(type, identifier, included) || {};
  const renderedAttributes = root ? attributes : includedAttributes;
  const renderedLinks = root ? links : includedLinks; // Resources without a valid identifier are actually not specified, but
  // otherwise responses that are no resource and thus do not have a valid
  // identifier could not be deserialized.

  if (!root && !identifier.valid) return null;
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, renderIdentifier(identifier)), renderedAttributes), deserializeRelationships(relationships, included)), deserializeRelationships(includedRelationships, included)), renderLinks(rootLinks, 'rootLinks')), renderLinks(renderedLinks));
};

const deserializeResources = function (resources, included, rootLinks) {
  let root = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  return compact(map(resources, resource => deserializeResource(resource, included, rootLinks, root)));
};

const deserialize = function () {
  return subject => {
    const {
      data,
      errors,
      included,
      links: rootLinks
    } = subject;

    if (isPresent(data) && isPresent(errors)) {
      throw new Error('The keys `data` and `errors` must not coexist in a single document.');
    }

    if (isPresent(data)) {
      if (Array.isArray(data)) {
        return deserializeResources(data, included, rootLinks, true);
      } else if (isPlainObject(data)) {
        return deserializeResource(data, included, rootLinks, true);
      }
    } else if (isPresent(errors)) {
      if (Array.isArray(errors)) return errors;
    }

    return {};
  };
};

const _excluded = ["included"];

const removeDuplicateIncludes = included => {
  const includeDictionary = {};
  return reduce(included, (result, include) => {
    const {
      name,
      value
    } = extractIdentifier(include);
    const path = [include.type, name, value].join('/');

    if (!includeDictionary[path]) {
      result.push(include);
      includeDictionary[path] = true;
    }

    return result;
  }, []);
};

const extractResourceInformation = (resource, attributeNames, relationshipNames) => {
  const identifier = extractIdentifier(resource);
  const permittedAttributes = attributeNames == null ? omit(resource, ['id', 'lid']) : pick(resource, [...attributeNames, ...relationshipNames]);
  const polymorphicType = resource[COMPEON_API_JS_TYPE];
  const [relationships, attributes] = partition(permittedAttributes, (_, key) => includes(relationshipNames, key));
  return {
    attributes,
    identifier,
    relationships,
    polymorphicType
  };
};

const renderResourceAttribute = (key, attribute) => {
  if (isEmpty(attribute)) return null;
  return {
    [key]: attribute
  };
};

const renderResource = function (type) {
  let identifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let attributes = arguments.length > 2 ? arguments[2] : undefined;
  let relationships = arguments.length > 3 ? arguments[3] : undefined;
  let included = arguments.length > 4 ? arguments[4] : undefined;
  return _objectSpread2({
    data: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, renderIdentifier(identifier)), renderResourceAttribute('attributes', attributes)), renderResourceAttribute('relationships', relationships)), {}, {
      type
    })
  }, renderResourceAttribute('included', included));
};

const serializeRelationships = (relationships, options) => reduce(relationships, (result, value, key) => {
  const relationshipOptions = options[key];
  if (!isPlainObject(relationshipOptions)) return result;
  if (!relationshipOptions.type) throw `You did not specify a type for the relationship '${key}'`;
  const {
    data,
    included
  } = Array.isArray(value) ? serializeResources(relationshipOptions.type, value, relationshipOptions) : serializeResource(relationshipOptions.type, value, relationshipOptions);
  if (!isEmpty(data)) result.relationships[key] = {
    data
  };
  if (!isEmpty(included)) result.included = mergeArrays(result.included, included);
  return result;
}, {
  relationships: {},
  included: []
});

const serializeResource = function (type, resource, options) {
  let root = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  const attributeOptions = options.attributes;
  const relationshipOptions = options.relationships || {};
  const relationshipNames = Object.keys(relationshipOptions);
  const {
    attributes,
    identifier,
    relationships,
    polymorphicType
  } = extractResourceInformation(resource, attributeOptions, relationshipNames);
  const {
    included,
    relationships: serializedRelationships
  } = serializeRelationships(relationships, relationshipOptions);

  if (type === 'polymorphic') {
    if (root) throw 'You should use separate serializers to render different root resources.';

    if (polymorphicType) {
      type = polymorphicType;
    } else if (identifier.valid) {
      throw `You did not specify a type for the resource with ${identifier.name} ${identifier.value}.`;
    } else {
      throw 'You did not specify a type for one of the polymorphic resources.';
    }
  }

  if (root) {
    return renderResource(type, identifier, attributes, serializedRelationships, removeDuplicateIncludes(included));
  }

  if (!identifier.valid) return {};

  if (!isEmpty(attributes)) {
    const resource = renderResource(type, identifier, attributes, serializedRelationships);
    included.push(resource.data);
  }

  return renderResource(type, identifier, null, null, included);
};

const serializeResources = function (type, resources, options) {
  let root = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;

  const _reduce = reduce(resources, (result, value, key) => {
    const {
      data,
      included
    } = serializeResource(type, value, options, root);
    if (data) result.data.push(data);
    if (!isEmpty(included)) result.included = mergeArrays(result.included, included);
    return result;
  }, {
    data: []
  }),
        {
    included
  } = _reduce,
        result = _objectWithoutProperties(_reduce, _excluded);

  if (!included) return result;
  return _objectSpread2(_objectSpread2({}, result), {}, {
    included: removeDuplicateIncludes(included)
  });
};

const serialize = function (type) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!type) throw 'You did not specify a type for the root resource.';
  return subject => {
    if (isPlainObject(subject)) return serializeResource(type, subject, options, true);
    if (Array.isArray(subject)) return serializeResources(type, subject, options, true);
    return renderResource(type);
  };
};

export { deserialize, serialize, withPolymorphicType };
